import React from 'react';
import {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    IonContent,
    IonCard,
    IonCardContent,
    IonItem,
    IonLabel,
    IonList,
    IonThumbnail,
    IonNote
} from '@ionic/react';
import { closeOutline, trendingUp, trendingDown } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { RiQrCodeLine, RiSendPlaneLine } from 'react-icons/ri';
import { GiChart } from "react-icons/gi";
import { Asset, PaymentDestinationInfo, WalletInfoState } from '@/types/types';
import { Provider, useSelector } from 'react-redux';
import { dispatch, store } from '@/services/redux/store';
import { useAccountDetailsQuery } from '@/services/api/stellar';
import { formatConversion } from '@/services/utils/formatConversion';
import supportedCurrencies from '@/currencies.json';
import { closeModal } from '@/services/redux/slices';
import RecipientPage from '../recipients/RecipientPage';
import { resetPaymentDestinationInfo, setPaymentDestinationInfo } from '@/services/redux/slices/paymentDestinationInfo/paymentDestinationInfo';
import QrCodePageStellarToken from '../qr_code/QrCodePageStellarToken';
import { setSelectedAsset } from '@/services/redux/slices/selected-asset/selected-asset';
import MarketInfoPage from '../accounts-details/MarketInfoPage';


const Page: React.FC = () => {
    const { t } = useTranslation('pages/cryptoAccount');
    const nav = document.querySelector("ion-nav");
    const walletInfo: WalletInfoState = useSelector((state: any) => state.walletInfo);
    const supportedAssets: Asset[] = useSelector((state: any) => state.supportedAssets.supportedAssets);
    const userInfo = useSelector((state: any) => state.user);
    const coinGeckoQuotes = useSelector((state: any) => state.quotes.quotes);

    const { data: accountDetails } = useAccountDetailsQuery(walletInfo.account_key, { skip: (!walletInfo.has_key) });

    // Get balances from account details
    const balances = accountDetails?.details?.balances;
    const currentCurrency = userInfo.currency_code || 'USD';
    const quotesIsSuccess = !!coinGeckoQuotes && Object.keys(coinGeckoQuotes).length > 0; 

    // Helper functions
    const formatBalance = (amount: number): string => {
        return formatConversion(amount).toString();
    };

    const getCurrencySymbol = (code: string): string => {
        const currency = supportedCurrencies.find(curr => curr.code === code);
        return currency?.symbol || '$';
    };

    // Dynamic font size based on balance length
    const getDynamicFontSize = (balanceText: string): string => {
        const length = balanceText.length;
        if (length <= 8) return '2rem';
        if (length <= 12) return '1.75rem';
        if (length <= 16) return '1.5rem';
        return '1.25rem';
    };

    const close = () => {
        dispatch(closeModal());
    }

    const goToRecipientPage = (asset: Asset) => {
        if (nav) {
            const destinationInfo: PaymentDestinationInfo = {
                destination_address: undefined,
                destination_amount: undefined,
                destination_asset: asset,
                destination_username: undefined
            }
            dispatch(resetPaymentDestinationInfo());
            dispatch(setPaymentDestinationInfo(destinationInfo));
            nav?.push(() => <RecipientPage />);
        }
    }

    const presentQRReceiveModal = async (asset: Asset) => {
        dispatch(setSelectedAsset(asset));
        nav?.push(() => <QrCodePageStellarToken />)
    }

    const showAssetChart = (asset: Asset) => {
        dispatch(setSelectedAsset(asset));
        nav?.push(() => <MarketInfoPage asset={asset} />)
    }

    // Calculate total crypto balance using CoinGecko quotes
    const totalCryptoBalance = React.useMemo((): number => {
        if (!quotesIsSuccess || !userInfo || !balances || !supportedAssets) {
            return 0;
        }

        let totalBalance = 0;

        // Filter and calculate only for crypto assets
        supportedAssets.forEach((asset: Asset) => {
            // Only process crypto assets
            if (asset.type !== 'crypto') return;

            const assetBalance = balances[asset.identifier];
            if (assetBalance && userInfo && asset.coingecko_id) {
                // CoinGecko data structure: coinGeckoQuotes[coingecko_id][currency_code]
                const quoteData = coinGeckoQuotes[asset.coingecko_id];
                if (quoteData) {
                    const currencyKey = userInfo.currency_code.toLowerCase();
                    const quoteValue = quoteData[currencyKey];
                    if (quoteValue) {
                        const amountToAdd = quoteValue * assetBalance.available.toNumber();
                        totalBalance += amountToAdd;
                    }
                }
            }
        });

        return totalBalance;
    }, [quotesIsSuccess, coinGeckoQuotes, userInfo, balances, supportedAssets]);

    // Calculate total portfolio 24h change from CoinGecko quotes
    const totalChange = React.useMemo((): number => {
        if (!quotesIsSuccess || !userInfo || !balances || !supportedAssets) {
            return 0;
        }

        let totalCurrentValue = 0;
        let totalPreviousValue = 0;

        supportedAssets.forEach((asset: Asset) => {
            if (asset.type !== 'crypto') return;

            const assetBalance = balances[asset.identifier];
            if (assetBalance && userInfo && asset.coingecko_id) {
                // CoinGecko data structure: coinGeckoQuotes[coingecko_id][currency_code]
                const quoteData = coinGeckoQuotes[asset.coingecko_id];
                if (quoteData) {
                    const currencyKey = userInfo.currency_code.toLowerCase();
                    const currentPrice = quoteData[currencyKey];
                    const changePercent = quoteData[`${currencyKey}_24h_change`];
                    
                    if (currentPrice !== undefined && changePercent !== undefined) {
                        const balance = assetBalance.available.toNumber();
                        const currentValue = currentPrice * balance;
                        const previousPrice = currentPrice / (1 + changePercent / 100);
                        const previousValue = previousPrice * balance;
                        
                        totalCurrentValue += currentValue;
                        totalPreviousValue += previousValue;
                    }
                }
            }
        });

        if (totalPreviousValue === 0) return 0;
        return ((totalCurrentValue - totalPreviousValue) / totalPreviousValue) * 100;
    }, [quotesIsSuccess, coinGeckoQuotes, userInfo, balances, supportedAssets]);

    const isPositive = totalChange >= 0;

    return (
        <IonPage>
            <IonHeader className="ion-no-border">
                <IonToolbar>
                    <IonButtons slot='start'>
                        <IonButton onClick={() => { close() }} color='primary'>
                            <IonIcon
                                slot="icon-only"
                                icon={closeOutline}
                            />
                        </IonButton>
                    </IonButtons>
                    <IonTitle>{t('title')}</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">
                <div className="max-w-2xl mx-auto">
                    {/* Total Balance Card with Chart */}
                    <IonCard
                        className="relative overflow-hidden rounded-3xl shadow-2xl border-0 m-0"
                        style={{
                            background: 'linear-gradient(135deg, var(--ion-color-primary) 0%, var(--ion-color-primary-shade) 50%, var(--ion-color-primary-tint) 100%)',
                            '--background': 'transparent',
                        }}
                    >
                        <IonCardContent className="p-6 relative">
                            <div className="relative z-10">
                                <div className="flex flex-col mb-6">
                                    <div className="flex flex-col items-start">
                                        <p className="text-sm font-medium mb-3 text-left" style={{ color: 'white' }}>
                                            {t('totalPortfolioValue')}
                                        </p>

                                        {/* Balance Display */}
                                        <div className="flex items-center justify-between w-full" style={{ margin: '24px 0' }}>
                                            <span
                                                className="text-left"
                                                style={{
                                                    fontSize: getDynamicFontSize(`${getCurrencySymbol(currentCurrency)}${formatBalance(totalCryptoBalance)}`),
                                                    fontWeight: '900',
                                                    lineHeight: '1.1',
                                                    color: 'white'
                                                }}
                                            >
                                                {getCurrencySymbol(currentCurrency)}{formatBalance(totalCryptoBalance)}
                                            </span>
                                        </div>

                                        {/* 24h Change */}
                                        {/* <div className="flex items-center gap-2" style={{ marginBottom: '16px' }}>
                                            <IonIcon
                                                icon={isPositive ? trendingUp : trendingDown}
                                                style={{
                                                    color: isPositive ? '#4ade80' : '#f87171',
                                                    fontSize: '16px'
                                                }}
                                            />
                                            <span
                                                style={{
                                                    color: isPositive ? '#4ade80' : '#f87171',
                                                    fontSize: '14px',
                                                    fontWeight: '600'
                                                }}
                                            >
                                                {isPositive ? '+' : ''}{totalChange.toFixed(2)}% (24h)
                                            </span>
                                        </div> */}
                                    </div>
                                </div>
                            </div>
                        </IonCardContent>
                    </IonCard>

                    {/* Crypto Assets List */}
                    <div style={{ marginTop: '24px' }}>
                      
                                {supportedAssets
                                    .filter((asset: Asset) => asset.type === 'crypto')
                                    .map((asset: Asset) => {
                                        const inAccount = !!balances?.[asset.identifier];
                                        const balance = balances?.[asset.identifier];
                                        
                                        // Get real 24h price change from CoinGecko quotes
                                        const quoteData = asset.coingecko_id ? coinGeckoQuotes[asset.coingecko_id] : null;
                                        const currencyKey = currentCurrency.toLowerCase();
                                        const priceChange = quoteData?.[`${currencyKey}_24h_change`] || 0;
                                        const isPricePositive = priceChange >= 0;

                                        // Calculate currency value if available
                                        let usdValue = 0;
                                        if (inAccount && balance && quotesIsSuccess && quoteData) {
                                            const currentPrice = quoteData[currencyKey];
                                            if (currentPrice) {
                                                usdValue = currentPrice * balance.available.toNumber();
                                            }
                                        }

                                        return (
                                            <IonCard key={asset.identifier}>
                                                <IonCardContent>
                                                    <IonItem lines="none">
                                                        <IonThumbnail className="accountsPage_Thumbnail_Flag" slot="start">
                                                            <img src={asset.icon} alt={asset.display_code} />
                                                        </IonThumbnail>

                                                        <IonLabel className="ion-text-start">
                                                            {inAccount && balance && (
                                                                <IonNote className="text-xs ">
                                                                    {balance.available.toFixed(4)} {asset.code}
                                                                </IonNote>
                                                            )}
                                                        </IonLabel>

                                                        <div slot='end' className="ion-text-end">
                                                            {inAccount && balance ? (
                                                                <>
                                                                    <p className="font-bold text-gray-900 dark:text-white m-0 ion-text-end">
                                                                        {getCurrencySymbol(currentCurrency)}{usdValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    </p>
                                                                    <div className="flex items-center justify-end gap-1 mt-1">
                                                                        <IonIcon
                                                                            icon={isPricePositive ? trendingUp : trendingDown}
                                                                            style={{
                                                                                color: isPricePositive ? '#16a34a' : '#dc2626',
                                                                                fontSize: '12px'
                                                                            }}
                                                                        />
                                                                        <span
                                                                            style={{
                                                                                color: isPricePositive ? '#16a34a' : '#dc2626',
                                                                                fontSize: '12px',
                                                                                fontWeight: '600'
                                                                            }}
                                                                        >
                                                                            {isPricePositive ? '+' : ''}{priceChange.toFixed(2)}%
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex gap-2 items-center mt-2">
                                                                        <IonButton
                                                                            onClick={() => goToRecipientPage(asset)}
                                                                            fill='clear'
                                                                            color={'primary'}
                                                                            size="small"
                                                                        >
                                                                            <RiSendPlaneLine size={20} />
                                                                        </IonButton>

                                                                        <IonButton
                                                                            onClick={() => { presentQRReceiveModal(asset) }}
                                                                            fill='clear'
                                                                            color={'primary'}
                                                                            size="small"
                                                                        >
                                                                            <RiQrCodeLine size={20} />
                                                                        </IonButton>

                                                                        <IonButton
                                                                            onClick={() => { showAssetChart(asset) }}
                                                                            fill='clear'
                                                                            color={'primary'}
                                                                            size="small"
                                                                        >
                                                                            <GiChart size={20} />
                                                                        </IonButton>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <IonButton
                                                                    onClick={() => { }}
                                                                    color={'primary'}
                                                                    fill='outline'
                                                                >
                                                                    {t('addAccount')}
                                                                </IonButton>
                                                            )}
                                                        </div>
                                                    </IonItem>
                                                </IonCardContent>
                                            </IonCard>
                                        );
                                    })}
                       
                        
                    </div>
                </div>
            </IonContent>
        </IonPage>
    );
};

const CryptoAccountPage: React.FC = () => (
    <Provider store={store}>
        <Page />
    </Provider>
);

export default CryptoAccountPage;
